import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_DOMAIN =
  process.env.REACT_APP_BACKEND_DOMAIN ?? "ENV BACKEND_DOMAIN DEFAULT";
const BACKEND_PORT =
  process.env.REACT_APP_BACKEND_PORT ?? "ENV BACKEND_PORT DEFAULT";

const fetchUsersByUsername = async (username: string) => {
  if (!username) return [];
  // Use improved endpoint with query param
  const res = await fetch(
    `${BACKEND_DOMAIN}${BACKEND_PORT}/auth0_user_search?username=${username}`
  );
  if (!res.ok) return [];
  const data = await res.json();
  // Now returns array of { user_id, nickname, picture }
  return Array.isArray(data) ? data : [];
};

function ProfileSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setShowDropdown(!!value && value.length >= 3);
    setLoading(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    if (value.length >= 3) {
      debounceTimeout.current = setTimeout(async () => {
        const users = await fetchUsersByUsername(value);
        setResults(users);
        setLoading(false);
      }, 250);
    } else {
      setResults([]);
      setLoading(false);
    }
  };

  const handleSelect = (user: any) => {
    setShowDropdown(false);
    setQuery("");
    // Only use the part after the '|' in user_id
    const id = user.user_id.includes("|")
      ? user.user_id.split("|")[1]
      : user.user_id;
    navigate(`/profile/${id}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) {
      handleSelect(results[0]);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-2">
      <form onSubmit={handleSubmit} className="w-full max-w-md relative">
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="profile-search" className="font-semibold text-lg">
            Search by username
          </label>
          <div
            className="relative flex items-center"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            tabIndex={0}
            aria-label="Search info"
          >
            <span className="ml-1 cursor-pointer text-gray-400 border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center bg-white hover:bg-gray-100">
              i
            </span>
            {showTooltip && (
              <div className="absolute left-6 top-1 z-20 w-72 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                <strong>How search works:</strong>
                <ul className="list-disc ml-5 mt-1">
                  <li>
                    Search looks up users by <b>username</b> (not email or ID).
                  </li>
                  <li>
                    You must enter at least <b>3 characters</b> to search.
                  </li>
                  <li>Results are partial and case-insensitive.</li>
                  <li>Only users with a matching username will appear.</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <input
          id="profile-search"
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Type at least 3 characters..."
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:outline-none text-lg shadow"
          autoComplete="off"
          minLength={3}
        />
        {query.length > 0 && query.length < 3 && (
          <div className="text-xs text-red-500 mt-1">
            Please enter at least 3 characters to search.
          </div>
        )}
        {showDropdown && (
          <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                No users found
              </div>
            ) : (
              results.map((user) => (
                <button
                  key={user.user_id}
                  type="button"
                  className="w-full text-left px-4 py-2 hover:bg-primary-100 transition-colors"
                  onClick={() => handleSelect(user)}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={user.picture}
                      alt={user.nickname}
                      className="w-8 h-8 rounded-full object-cover border"
                    />
                    <span className="font-medium">{user.nickname}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default ProfileSearch;
